import { queryOptions, useQueries } from '@tanstack/react-query';

import type { Role } from '@crossnokaye/typescript-sdk/models/components';

import { AtlasClientFunction } from '@/client/types';
import { useAtlasClient } from '@/hooks/use-atlas-client';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import { useUser } from '@/hooks/use-user';
import { rolePrivilegeInfoQuery } from '@/queries/iam/role-privilege-info-query';
import { userAccessRulesQuery } from '@/queries/iam/user-access-rules-query';

// Regex to match scope pattern: /orgs/{orgId}/facilities/{facilityId}/agents/{agentId}
// orgId in group 1, facilityId in group 2, agentId in group 3
const SCOPE_REGEX = /^\/orgs\/(.*)\/facilities\/(.*)\/agents\/(.*)$/;

// Create reusable query options with caching optimizations for static role data
const userAccessRulesOptions = (userId: string, client: AtlasClientFunction) =>
  queryOptions({
    ...userAccessRulesQuery({ userId: userId || '', client }),
    enabled: !!userId,
    // Roles don't change during session, so disable automatic refetching
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // Note: staleTime and gcTime are already set to Infinity in userAccessRulesQuery
  });

const rolePrivilegeInfoOptions = (client: AtlasClientFunction) =>
  queryOptions({
    ...rolePrivilegeInfoQuery({ client }),
    // Role definitions don't change during session
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // Note: staleTime and gcTime are already set to Infinity in rolePrivilegeInfoQuery
  });

/**
 * Hook to get the user's actual roles for the current facility.
 * This filters the user's access rules by the current facility scope and returns
 * only the roles they have at this specific facility, with display names.
 * Uses TanStack Query's useQueries with combine for optimal parallel fetching.
 */
export const useUserFacilityRoles = () => {
  const client = useAtlasClient();
  const { userId } = useUser();
  const { organizationId, agentId } = useCurrentFacility();

  return useQueries({
    queries: [userAccessRulesOptions(userId || '', client), rolePrivilegeInfoOptions(client)],
    combine: (results) => {
      const [accessRulesResult, roleInfoResult] = results;

      // Check if both queries are successful and have data
      if (
        !accessRulesResult.data?.accessRules ||
        !roleInfoResult.data?.roles ||
        !organizationId ||
        !agentId
      ) {
        return {
          data: undefined,
          isLoading: accessRulesResult.isLoading || roleInfoResult.isLoading,
          isError: accessRulesResult.isError || roleInfoResult.isError,
          error: accessRulesResult.error || roleInfoResult.error,
          isSuccess: false,
        };
      }

      // Filter access rules by current facility scope
      const facilityAccessRules = accessRulesResult.data.accessRules.filter((rule) => {
        const match = rule.scope.match(SCOPE_REGEX);
        return match && match[1] === organizationId && match[3] === agentId;
      });

      // Get unique role names from the filtered rules
      const roleNames = new Set<string>();
      facilityAccessRules.forEach((rule) => {
        rule.roles.forEach((role) => roleNames.add(role));
      });

      // Map role names to Role objects with display names
      const roles: Role[] = Array.from(roleNames)
        .map((roleName) => {
          const roleInfo = roleInfoResult.data?.roles.find((r) => r.name === roleName);
          return roleInfo || { name: roleName, displayName: roleName, privileges: [] };
        })
        .sort((a, b) => a.displayName.localeCompare(b.displayName)); // Sort alphabetically for priority

      return {
        data: { roles },
        isLoading: false,
        isError: false,
        error: null,
        isSuccess: true,
      };
    },
  });
};
